import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { projectAPI, authAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { VoteButtons } from '../components/VoteButtons';
import { CommentSection } from '../components/CommentSection';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { 
  Github, ExternalLink, Calendar, User, ArrowLeft, Trash2, Edit, 
  FileText, Image as ImageIcon, Loader2 
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchCurrentUser();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await projectAPI.getOne(projectId);
      setProject(response.data);
    } catch (error) {
      toast.error('Project not found');
      navigate('/projects');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      // Not logged in
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await projectAPI.delete(projectId);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const isOwner = currentUser?.user_id === project?.author_id;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-detail-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/projects" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </Link>

        {/* Project Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 mb-6">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="project-title">
                {project.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <Link to={`/users/${project.author_id}`} className="flex items-center gap-2 hover:text-indigo-600">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={project.author_picture} />
                    <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                      {project.author_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {project.author_name}
                </Link>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <VoteButtons
                targetType="project"
                targetId={project.project_id}
                initialUpvotes={project.upvotes}
                initialDownvotes={project.downvotes}
                userVote={project.user_vote}
              />
            </div>
          </div>

          {/* Tags */}
          {project.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {project.tags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Links */}
          <div className="flex flex-wrap gap-3 mb-6">
            {project.github_url && (
              <a
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="github-link"
                data-testid="project-github-link"
              >
                <Github className="w-4 h-4" />
                View on GitHub
              </a>
            )}
            {project.demo_url && (
              <a
                href={project.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                data-testid="project-demo-link"
              >
                <ExternalLink className="w-4 h-4" />
                Live Demo
              </a>
            )}
          </div>

          {/* Description */}
          <div className="prose-content text-slate-700">
            <p className="whitespace-pre-wrap" data-testid="project-description">{project.description}</p>
          </div>

          {/* Owner Actions */}
          {isOwner && (
            <div className="flex gap-2 mt-6 pt-6 border-t border-slate-200">
              <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${projectId}/edit`)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your project and all associated comments.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {/* Images */}
        {project.images?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Screenshots
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {project.images.map((img, index) => (
                <a
                  key={index}
                  href={img}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg overflow-hidden bg-slate-100"
                >
                  <img
                    src={img}
                    alt={`Screenshot ${index + 1}`}
                    className="w-full aspect-video object-cover hover:scale-105 transition-transform"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Files */}
        {project.files?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Attached Files
            </h2>
            <div className="space-y-2">
              {project.files.map((file, index) => (
                <a
                  key={index}
                  href={`${BACKEND_URL}/api/files/${file.file_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <FileText className="w-5 h-5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{file.filename}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8">
          <CommentSection
            targetType="project"
            targetId={projectId}
            currentUser={currentUser}
          />
        </div>
      </main>
    </div>
  );
}
